<template>
  <div>
    <helpers-section-header class="mt-6 mb-2"> 13F Filing Signal Rules </helpers-section-header>
    <p class="text-body-2 mb-6">
      13F filings are quarterly reports required of all institutional investment managers with
      over $100M in assets. They disclose long equity holdings at the end of each quarter.
      This signal buys stocks that appear in the top holdings of your selected group of managers,
      based on the criteria you configure below. The core idea: follow what sophisticated
      institutions are accumulating.
    </p>

    <!-- Fund Group Manager -->
    <helpers-section-header class="font-weight-light"> Fund Selection </helpers-section-header>
    <core-fund-group-manager
      ref="fundGroupManager"
      :filer-group="criteria.filer_group"
      backtester
      whale-trader
      flat
      no-padding
      @update:filer-group="
        newVal => {
          criteria.filer_group.id = newVal.id
          criteria.filer_group.filers = newVal.filers
        }
      "
    >
      <template #predefined-groups>
        <!-- Whalescore filers -->
        <v-menu>
          <template #activator="{ props }">
            <v-btn small class="ma-1" v-bind="props"> Whalescore filers </v-btn>
          </template>
          <v-list density="compact" class="py-0">
            <v-list-item
              v-for="wwFiler in whalescoreFilersItems"
              :key="wwFiler.id"
              :disabled="!subscription || subscription === 'STANDARD'"
              @click="WhaleScoreFilerSelected(wwFiler)"
            >
              <v-list-item-title>{{ wwFiler.description }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Choose Filers by Performance -->
        <v-btn small class="ma-1" @click="fundPerformanceDialog = true">
          Dynamic Fund Selection
        </v-btn>
        <v-dialog v-model="crowded_dialog" persistent max-width="370">
          <template #activator="{ props }">
            <v-btn class="ma-1" small v-bind="props"> Unique Trades </v-btn>
          </template>
          <v-card>
            <v-card-title> Unique Trades </v-card-title>
            <v-card-text>
              <p>
                Stocks are ranked according to 1) past 12 months price performance and 2) how widely
                held the stocks are based on 13F filings. Stocks with higher performance will be
                ranked higher. Stocks less widely held will be ranked higher.
              </p>
              <v-row density="compact">
                <v-col cols="12">
                  <v-checkbox
                    v-model="criteria.crowded_stocks"
                    density="compact"
                    label="Select Stocks Based on Unique Trade Rankings"
                    @click="CrowdedStockSelected()"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="criteria.crowded_stocks_avg_min"
                    variant="outlined"
                    density="compact"
                    label="Minimum Avg. Ranking"
                    :disabled="!criteria.crowded_stocks"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="criteria.crowded_stocks_held_min"
                    variant="outlined"
                    density="compact"
                    label="Minimum % Held Ranking"
                    :disabled="!criteria.crowded_stocks"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="criteria.crowded_stocks_perf_min"
                    variant="outlined"
                    density="compact"
                    label="Minimum 12m Perf. Ranking"
                    :disabled="!criteria.crowded_stocks"
                  />
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn small @click="crowded_dialog = false"> OK </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-btn small class="ma-1" @click="HeatMapSelected()"> Use Heat Map Stocks </v-btn>
      </template>
    </core-fund-group-manager>

    <helpers-section-header class="font-weight-light layout">
      Report options
      <v-spacer />
      <v-btn text color="error" class="ma-0" @click="resetBacktesterOptions()">
        Reset options
      </v-btn>
    </helpers-section-header>
    <BasicOptions />

    <fund-performance-dialog ref="FundPerformanceDialog" whale-trader />
  </div>
</template>

<script>
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import { mapWritableState } from 'pinia'
import cloneDeep from 'lodash/cloneDeep'
import FundPerformanceDialog from '@/components/page/whale-backtester/signals/_WtFundPerformanceDialog'
import AccessMixin from '@/mixins/access'
import { mapStoreState, mapStoreGetters } from '@/store/helpers'
import BasicOptions from '@/components/page/whale-backtester/signals/_F13Basic'
import { defaultSelectedGroup } from '@/data/constants'
export default {
  components: {
    BasicOptions,
    FundPerformanceDialog
  },
  mixins: [AccessMixin],

  data() {
    return {
      crowded_dialog: false,
      and_or: 'or',
      whalescoreFilersItems: [
        {
          description: 'Top 30 WhaleScores each quarter (equal-wt)',
          value: 1,
          requires_subscription: true
        },
        {
          description: 'Top 30 WhaleScores each quarter (mgr-wt)',
          value: 2,
          requires_subscription: true
        },
        {
          description:
            'All having a 1-yr avg WhaleScore (equal-weight) that beat the S&P 500 each quarter',
          value: 3,
          requires_subscription: true
        },
        {
          description:
            'All funds having a 1-yr avg WhaleScore (manager-weight) that beat the S&P 500 each quarter',
          value: 4,
          requires_subscription: true
        },
        {
          description:
            'All funds having a 3-yr avg WhaleScore (equal-weight) that beat the S&P 500 each quarter',
          value: 5,
          requires_subscription: true
        },
        {
          description:
            'All funds having a 3-yr avg WhaleScore (manager-weight) that beat the S&P 500 each quarter',
          value: 6,
          requires_subscription: true
        },
        {
          description: 'Custom Testing',
          value: 7,
          requires_subscription: true
        },
        {
          description: 'Custom Testing2',
          value: 458,
          requires_subscription: true
        }
      ],
      dialog: false,
      fundPerformanceDialog: false
    }
  },
  computed: {
    ...mapStoreState({
      quarters: state => state.ww.quarters.unshift({ id: 0, description: 'All Available Dates' }),
      groups: state => state.user.groups
    }),
    ...mapWritableState(useAppStore, ['whaleTraderCriteria', 'signalView', 'exit_time_periods']),

    // Computed criteria based on ruleType prop - avoids prop mutation warning
    criteria() {
      return this.whaleTraderCriteria.signals.f13_signal
    },
    appcriteria() {
      return this.whaleTraderCriteria
    },
    enabled() {
      return this.whaleTraderCriteria.signals.f13_signal.enabled
    },
    /* ...mapFields({
      app_criteria: 'app.whaleTraderCriteria',
      criteria: 'app.whaleTraderCriteria.signals.f13_signal',
      enabled: 'app.whaleTraderCriteria.signals.f13_signal.enabled',
      signalView: 'app.signalView'
    }),*/
    ...mapStoreGetters({
      subscription: 'user/subscription'
    })
  },
  methods: {
    WhaleScoreFilerSelected(group) {
      this.criteria.whalescore = group
      this.criteria.heat_map = false
      this.criteria.crowded_stocks = false
      this.criteria.filer_performance.top_performing_type = {
        description: 'Do not select filers based on performance',
        value: 0,
        requires_subscription: true
      }
      this.criteria.filer_performance.selected = false
      this.clear_group()
    },
    clear_group() {
      useAppStore().setSelectedGroup(null)
      this.criteria.filer_group = cloneDeep(defaultSelectedGroup)
    },
    HeatMapSelected() {
      this.criteria.whalescore = null
      this.criteria.heat_map = true
      this.criteria.crowded_stocks = false
      this.criteria.filer_performance.top_performing_type = {
        description: 'Do not select filers based on performance',
        value: 0,
        requires_subscription: true
      }
      this.criteria.filer_performance.selected = false
      this.clear_group()
    },
    CrowdedStockSelected() {
      if (this.criteria.crowded_stocks) {
        this.criteria.whalescore = null
        this.criteria.heat_map = false
        this.criteria.filer_performance.top_performing_type = {
          description: 'Do not select filers based on performance',
          value: 0,
          requires_subscription: true
        }
        this.criteria.filer_performance.selected = false

        this.clear_group()
      }
    }
  }
}
</script>
